'use client';

import React, { useEffect, useRef, useState } from 'react';
import BlastSidebar from './components/BlastSidebar';
import HeroManifesto from './components/HeroManifesto';
import StatsImpact from './components/StatsImpact';
import SurfaceBreakdown from './components/SurfaceBreakdown';
import CostComparison from './components/CostComparison';
import BlastFooter from '@/components/Footer';

export default function BlastPage() {
  const [activeSection, setActiveSection] = useState('hero');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sectionRefs = {
    hero: useRef<HTMLElement>(null),
    stats: useRef<HTMLElement>(null),
    surfaces: useRef<HTMLElement>(null),
    comparison: useRef<HTMLElement>(null),
  };

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    Object.entries(sectionRefs).forEach(([key, ref]) => {
      if (!ref.current) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(key);
            }
          });
        },
        { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
      );
      observer.observe(ref.current);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // Reveal on scroll
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal-section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
          }
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0.05 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const ref = sectionRefs[sectionId as keyof typeof sectionRefs];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-arctic-white">
      {/* Company Header */}
      <div
        className="flex items-center gap-4 px-8 py-5"
        style={{
          background: 'var(--arctic-white)',
          borderBottom: '1px solid var(--mist-gray)',
        }}
      >
        <img
          src="/assets/images/Screenshot_2026-04-27_at_8.38.59_PM-1777340378778.png"
          alt="Lake Country Cleaning logo"
          style={{ height: '56px', width: 'auto', objectFit: 'contain' }}
        />
        <span
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
            color: 'var(--slate)',
            letterSpacing: '-0.02em',
          }}
        >
          Lake Country Cleaning
        </span>
      </div>

      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle navigation"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          {sidebarOpen ? (
            <>
              <path d="M18 6L6 18" /><path d="M6 6l12 12" />
            </>
          ) : (
            <>
              <path d="M3 12h18" /><path d="M3 6h18" /><path d="M3 18h18" />
            </>
          )}
        </svg>
      </button>

      {/* Overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <BlastSidebar
        activeSection={activeSection}
        onNavigate={scrollToSection}
        isOpen={sidebarOpen}
      />

      {/* Main Content */}
      <main className="blast-main">
        <HeroManifesto ref={sectionRefs.hero} />
        <StatsImpact ref={sectionRefs.stats} />
        <SurfaceBreakdown ref={sectionRefs.surfaces} />
        <CostComparison ref={sectionRefs.comparison} />
        <BlastFooter />
      </main>
    </div>
  );
}